import { PackageDataSpec } from '@cadstrom/cadstrom-shared';

import { clauseCode, qualifiedClauseCode } from './clause-label.js';

export interface ClauseTooltipContentProps {
	clause: PackageDataSpec.PackageDataSpecClause;
	/** Shows the property before the clause number. */
	qualified?: boolean;
	/** A note shown at the bottom, e.g. what clicking the chip does. */
	hint?: string;
}

/**
 * The tooltip body for a clause version. The header shows the clause number, version, and author; below it
 * are the short name, the description, and, for a retired clause, its retirement note if it has one.
 *
 * @param props - {@link ClauseTooltipContentProps}.
 * @returns The tooltip body.
 */
export function ClauseTooltipContent({ clause, qualified = false, hint }: ClauseTooltipContentProps) {
	const retired = clause.status === PackageDataSpec.PackageDataSpecStatus.Retired;
	const author = clause.createdBy === null ? null : (clause.createdBy.displayName ?? clause.createdBy.username);

	return (
		<div className='flex flex-col gap-3'>
			<div className='border-b border-gray-a6 pb-2'>
				<p className='text-base font-semibold'>
					{qualified ? qualifiedClauseCode(clause) : clauseCode(clause)}{' '}
					<span className='text-sm font-normal text-gray-a11'>
						v{clause.version}
						{author === null ? '' : ` by ${author}`}
						{retired ? ' · retired' : ''}
					</span>
				</p>
			</div>

			<div className='flex flex-col gap-1'>
				<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Clause shortname</p>
				<p className='font-mono text-sm [text-box:initial]'>{clause.shortName}</p>
			</div>

			<div className='flex flex-col gap-1'>
				<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Description</p>
				<p className='text-sm leading-relaxed [text-box:initial]'>{clause.description}</p>
			</div>

			{retired && clause.retirementComment !== null && clause.retirementComment !== '' && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Retirement note</p>
					<p className='text-sm leading-relaxed [text-box:initial]'>{clause.retirementComment}</p>
				</div>
			)}

			{hint !== undefined && <p className='text-2xs leading-normal text-gray-a10 [text-box:initial]'>{hint}</p>}
		</div>
	);
}
